import { Alert, Button, CircularProgress } from '@mui/material';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

import OtpService from '../../../services/otp.service';
import { GTM, GTMEvents } from '../../../utils/analytics';
import { useNotification } from '../../../utils/snackbar';
import { PhoneInputData } from '../../PhoneInput/PhoneInput';
import { OtpTriggerSource, UserPhoneVerificationType } from '../UserPhoneVerificationModal';
import { useUserVerificationSendCode } from '../useUserVerificationSendCode';

type UserVerificationSendCodeProps = {
  phone: PhoneInputData;
  onSuccess: (code: string) => void;
  onReSendCodeSuccess: () => void;
  previousCode?: string;
  type: UserPhoneVerificationType;
  triggerSource?: OtpTriggerSource;
};

enum OtpCheckErrorCodes {
  OTP_INVALID_PHONE_ERROR = 'OTP_INVALID_PHONE_ERROR',
  OTP_REQUEST_FAILED_ERROR = 'OTP_REQUEST_FAILED_ERROR',
  OTP_VERIFY_FAILED_ERROR = 'OTP_VERIFY_FAILED_ERROR',
  OTP_VERIFY_EXPIRED_ERROR = 'OTP_VERIFY_EXPIRED_ERROR',
  OTP_VERIFY_OUT_OF_RETRIES_ERROR = 'OTP_VERIFY_OUT_OF_RETRIES_ERROR',
  OTP_UNINITIATED_ERROR = 'OTP_UNINITIATED_ERROR',
  OTP_OLD_NUMBER_UNVERIFIED_ERROR = 'OTP_OLD_NUMBER_UNVERIFIED_ERROR',
  OTP_OLD_NUMBER_VERIFICATION_NOT_FOUND_ERROR = 'OTP_OLD_NUMBER_VERIFICATION_NOT_FOUND_ERROR',
  OTP_ALREADY_VERIFIED_ERROR = 'OTP_ALREADY_VERIFIED_ERROR',
  OTP_PHONE_NUMBER_BLOCKED_ERROR = 'OTP_PHONE_NUMBER_BLOCKED_ERROR',
}

const NUM_INPUTS = 6;
const OTP_INPUT_BOX_CLASSNAME = 'otp-input-box';
const otpService = new OtpService();

export const UserPhoneVerificationVerifyCode = ({
  phone,
  onSuccess,
  onReSendCodeSuccess,
  previousCode,
  type,
  triggerSource,
}: UserVerificationSendCodeProps) => {
  const { t } = useTranslation();
  const { showErrorNotification } = useNotification();

  const [code, setCode] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [isResent, setResent] = useState(false);

  const {
    onSubmit: onResend,
    isCtaDisabled: isResendDisabled,
    isLoading: isResendLoading,
    setPhoneError: setResendPhoneError,
    responseError: resendResponseError,
  } = useUserVerificationSendCode({
    onSuccess: () => {
      onReSendCodeSuccess?.();
      setResent(true);
    },
  });

  const formatNumber = (phoneNumber: PhoneInputData) =>
    `+${phoneNumber?.parsed?.countryCallingCode} ${phoneNumber?.parsed?.nationalNumber}`;

  const onChangeCode = (optValue) => {
    setResponseError('');
    setResendPhoneError(false);
    setCode(optValue);
  };

  const handleError = (error) => {
    const errorCode: OtpCheckErrorCodes = error?.data?.data?.errorCode;
    let message = error?.data?.data?.message;

    switch (errorCode) {
      // Keep original message for known errors.
      case OtpCheckErrorCodes.OTP_INVALID_PHONE_ERROR:
      case OtpCheckErrorCodes.OTP_REQUEST_FAILED_ERROR:
      case OtpCheckErrorCodes.OTP_VERIFY_FAILED_ERROR:
      case OtpCheckErrorCodes.OTP_VERIFY_EXPIRED_ERROR:
      case OtpCheckErrorCodes.OTP_VERIFY_OUT_OF_RETRIES_ERROR:
      case OtpCheckErrorCodes.OTP_UNINITIATED_ERROR:
      case OtpCheckErrorCodes.OTP_OLD_NUMBER_UNVERIFIED_ERROR:
      case OtpCheckErrorCodes.OTP_OLD_NUMBER_VERIFICATION_NOT_FOUND_ERROR:
      case OtpCheckErrorCodes.OTP_ALREADY_VERIFIED_ERROR:
      case OtpCheckErrorCodes.OTP_PHONE_NUMBER_BLOCKED_ERROR: {
        break;
      }

      // Unknown error.
      default: {
        message = t('common.general-error');

        showErrorNotification({
          message,
          error,
        });
      }
    }

    setResponseError(message);
  };

  const onSubmit = async () => {
    setResponseError('');

    if (code.length < NUM_INPUTS) {
      setResponseError(t('components.phone-verification.verify-code-too-short'));

      return;
    }

    setLoading(true);
    GTM.logEvent(GTMEvents.OTP_CODE_ENTERED, { trigger_source: triggerSource });

    try {
      await otpService.verifyCode(
        {
          phoneNumber: phone?.raw,
          code,
          savePhone: [UserPhoneVerificationType.CHANGE_PHONE_VERIFY_NEW_NUMBER, UserPhoneVerificationType.VERIFY_NEW_PHONE].includes(type),
          previousCode,
        },
        { silentFail: true },
      );

      onSuccess(code);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const focusOnFirstCodeInput = () => {
    const firstInputEl: HTMLElement = document.querySelector(`.${OTP_INPUT_BOX_CLASSNAME}`);

    firstInputEl?.focus();
  };

  return (
    <>
      <h2 className="user-verification-modal-title">{t('components.phone-verification.enter-the-code')}</h2>

      <div className="user-verification-modal-body pt-1">
        <span
          dangerouslySetInnerHTML={{
            __html: t('components.phone-verification.enter-the-code-sent-to', {
              phone: formatNumber(phone),
            }),
          }}
        />

        <div className="otp-container mt-3">
          <OtpInput
            inputStyle={classNames(OTP_INPUT_BOX_CLASSNAME, { error: !!responseError })}
            value={code}
            onChange={onChangeCode}
            numInputs={NUM_INPUTS}
            renderSeparator={<span>&nbsp;&nbsp;</span>}
            renderInput={(props, index) => (
              <input
                {...props}
                className={classNames(props.className, { filled: !!props.value })}
                onChange={(e) => {
                  props.onChange?.(e);

                  // Last item changed.
                  if (index === NUM_INPUTS - 1) {
                    (e.target as HTMLInputElement)?.blur();
                  }
                }}
              />
            )}
            shouldAutoFocus
          />
        </div>

        {responseError && (
          <Alert severity="error" variant="standard" className="mt-1-i color-danger">
            {responseError}
          </Alert>
        )}

        {/* Resend button. */}
        <div className="mt-3">
          {t('components.phone-verification.didnt-receive')}
          &nbsp;
          <Button
            variant="text"
            size="small"
            disabled={isResendDisabled}
            onClick={() => {
              setResent(false);
              setCode('');
              onResend(phone);
              focusOnFirstCodeInput();
            }}
            className="resend-button"
          >
            {t('components.phone-verification.resend')}
          </Button>
        </div>

        {/* Resend error. */}
        {resendResponseError && (
          <Alert severity="error" variant="standard" className="mt-1-i color-danger">
            {resendResponseError}
          </Alert>
        )}

        {/* Resend loading. */}
        {isResendLoading && (
          <Alert icon={false} severity="info" variant="standard" className="mt-1-i resending-alert">
            <CircularProgress size={20} />
            <span>{t('components.phone-verification.resending')}</span>
          </Alert>
        )}

        {/* Resend success. */}
        {isResent && !resendResponseError && (
          <Alert severity="success" variant="standard" className="mt-1-i color-success">
            {t('components.phone-verification.resend-success')}
          </Alert>
        )}

        {/* Actions */}
        <div className="user-verification-modal-actions mt-4">
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            fullWidth
            size="large"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress color="inherit" size={16} />}
          >
            {t('components.phone-verification.verify')}
          </Button>
        </div>
      </div>
    </>
  );
};
